import { CoreError } from './errors'
import { parseMoney } from './money'
import { parseQty } from './qty'

// What a number box does with typing (docs/plans/amount-rounding.md): it holds only digits and
// one decimal point, too many decimals round to what can be stored, and what the box shows
// when it is left is what gets saved. parseMoney and parseQty stay strict for everything else.

const DEVANAGARI_ZERO = 0x0966

/** "१२३" → "123" */
export function toAsciiDigits(value: string): string {
  return value.replace(/[०-९]/g, (d) => String(d.charCodeAt(0) - DEVANAGARI_ZERO))
}

/** Rounds a plain decimal string to `dp` places, half away from zero. */
export function roundDecimal(value: string, dp: number): string {
  const negative = value.startsWith('-')
  const [whole = '0', fraction = ''] = (negative ? value.slice(1) : value).split('.')
  if (fraction.length <= dp) return value
  const scaled = BigInt(whole + fraction.slice(0, dp).padEnd(dp, '0'))
  const rounded = Number(fraction[dp]) >= 5 ? scaled + 1n : scaled
  const digits = rounded.toString().padStart(dp + 1, '0')
  const out = dp === 0 ? digits : `${digits.slice(0, -dp)}.${digits.slice(-dp)}`
  return negative && rounded !== 0n ? `-${out}` : out
}

/** "Rs. 1,234.50" → "1234.50", "a1b2.3.4" → "12.34"; without `decimal`, digits only. */
export function numberChars(text: string, decimal = true): string {
  const plain = toAsciiDigits(text)
    .trim()
    .replace(/^(rs\.?|nrs\.?|npr|inr|रु\.?|रू\.?|₹)\s*/i, '')
  const kept = plain.replace(decimal ? /[^\d.]/g : /\D/g, '')
  const dot = kept.indexOf('.')
  return dot < 0 ? kept : kept.slice(0, dot + 1) + kept.slice(dot + 1).replace(/\./g, '')
}

/** The box's next text: text with no digit or point in it (a pasted word) changes nothing. */
export function keepTyped(raw: string, previous: string, decimal = true): string {
  const kept = numberChars(raw, decimal)
  return kept === '' && raw.trim() !== '' ? previous : kept
}

export interface AmountText {
  /** The box's text while it has focus. */
  text: string
  /** Paisa or milli-units, already rounded; null when blank. */
  value: number | null
  /** The box's text once it is left. */
  tidy: string
}

/**
 * Reads a number box at 2 decimals (money) or 3 (quantity). Null when the text cannot be
 * accepted: a word, or a number too large to hold. A fraction on a whole-number unit has no
 * value and clears when the box is left.
 */
export function readAmountText(raw: string, decimals: 2 | 3, wholeOnly = false): AmountText | null {
  const text = numberChars(raw)
  if (text === '' && raw.trim() !== '') return null
  if (!/\d/.test(text)) return { text, value: null, tidy: '' }
  const rounded = roundDecimal(text, decimals)
  try {
    if (decimals === 2) return { text, value: parseMoney(rounded), tidy: rounded }
    return { text, value: parseQty(rounded, !wholeOnly), tidy: rounded }
  } catch (error) {
    if (error instanceof CoreError && error.code === 'invalid_qty') {
      return { text, value: null, tidy: '' }
    }
    return null
  }
}
